import { h, JSX, Fragment } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import classNames from 'classnames';
import { ArrowDownIcon } from './Icons';

export interface DropdownItemProps<T> {
    value: T;
}

export interface DropdownLabelProps<T> {
    value: T;
}

export interface DropdownProps<T> {
    open: boolean;
    name: string;
    value: T;
    searchValue: string;
    options: T[];
    onOutsideClick: () => void;
    onDropdownClick: () => void;
    onSelect: (value: T) => void;
    dropdownLabelElement?: (props: DropdownLabelProps<T>) => JSX.Element;
    dropdownItemElement?: (props: DropdownItemProps<T>) => JSX.Element;
    searchParameter?: (option: T) => string;
    onSearchInput?: (value: string) => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Dropdown = (props: DropdownProps<any>): JSX.Element => {
    const DropdownLabel = props.dropdownLabelElement;
    const DropdownItem = props.dropdownItemElement;

    useEffect(() => {
        window.addEventListener('click', props.onOutsideClick);

        return () => {
            window.removeEventListener('click', props.onOutsideClick);
        };
    }, [props.onOutsideClick]);

    const focusRef = useRef<HTMLInputElement>();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const searchFilter = (option: any): boolean => {
        if (!props.searchParameter) {
            return true;
        }

        return props.searchParameter(option)?.toLowerCase().indexOf(props.searchValue.toLowerCase()) !== -1;
    };

    const formControlClasses =
        'form-control block w-full h-12 px-3 py-3 pr-10 transition duration-75 rounded-lg outline-none focus:border-primary-500 focus:ring-1 focus:ring-inset focus:ring-primary-500 disabled:opacity-70 border-gray-300';

    return (
        <div class="relative">
            <label
                class={classNames(
                    'cursor-text border rounded-base',
                    formControlClasses,
                    props.open ? 'hidden' : 'visible'
                )}
                for={props.name}
                onClick={(e) => {
                    e.stopPropagation();
                    props.onDropdownClick();
                    focusRef.current.focus();
                }}
            >
                <DropdownLabel value={props.value} />
            </label>
            {props.searchParameter && (
                <input
                    class={classNames(formControlClasses, props.open ? 'visible' : 'hidden')}
                    ref={focusRef}
                    name={props.name}
                    type="text"
                    value={props.searchValue}
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                    onInput={(e) => {
                        props.onSearchInput(e.currentTarget.value);
                    }}
                />
            )}
            <ArrowDownIcon
                class={classNames(
                    'absolute right-3 top-3 transition-transform',
                    props.open ? 'rotate-180' : 'rotate-0'
                )}
            />
            {props.open && (
                <div class="absolute z-10 top-full overflow-auto max-h-[200px] flex flex-col bg-white shadow-dropdown mt-2 rounded-base left-0 min-w-full">
                    {(() => {
                        const filteredOptions = props.options.filter(searchFilter);
                        let lastSection: string | null = null;

                        return filteredOptions.map((option, index) => {
                            const currentSection = option.section || null;
                            const showSectionHeader = currentSection && currentSection !== lastSection;
                            lastSection = currentSection;

                            return (
                                <Fragment key={index}>
                                    {showSectionHeader && (
                                        <div class="px-3 py-2 text-xs font-semibold text-gray-500 bg-gray-50 border-t border-gray-200 first:border-t-0">
                                            {currentSection}
                                        </div>
                                    )}
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            props.onSelect(option);
                                        }}
                                    >
                                        <DropdownItem value={option} />
                                    </button>
                                </Fragment>
                            );
                        });
                    })()}
                </div>
            )}
        </div>
    );
};
